import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap, useScrollLock } from '@/shared/hooks/useFocusTrap';
import { X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './Drawer.module.scss';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  side?: 'start' | 'end';
  children: ReactNode;
  className?: string;
}

/** Painel lateral. Em telas pequenas e o formato da navegacao principal. */
export function Drawer({ open, onClose, title, side = 'start', children, className }: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, open, onClose);
  useScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onMouseDown={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(styles.panel, styles[side], className)}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar">
            <Icon as={X} size={20} />
          </button>
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
